import { useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { JService } from "../api-service/ApiService";
import { filtersReducer, getOrgURLParam } from "../../utility-functions";
import { chevronLeft, downloadIcon } from "../../utility-functions/assetLoader";
import { Filters } from "../dashboard/Filters";
import GTable from "../design/components/GTable";
import Button from "../design/Button";
import "./DataRetentionAuditLog.css";
import { enqueueSnackbar } from "notistack";

const EVENT_TYPE_LABELS = {
  auto_deletion: "Auto-deletion",
  policy_change: "Policy change",
  manual_deletion: "Manual deletion",
};

const filtersList = [
  {
    name: "Event type",
    toggle: false,
    isSearch: false,
    payloadFor: "eventTypes",
  },
  {
    name: "Triggered by",
    toggle: false,
    isSearch: true,
    payloadFor: "triggeredBy",
  },
];

const initialState = {
  eventTypes: {
    all: [
      { id: "auto_deletion", name: "Auto-deletion" },
      { id: "policy_change", name: "Policy change" },
      { id: "manual_deletion", name: "Manual deletion" },
    ],
    selected: [],
  },
  triggeredBy: { all: [], selected: [] },
  currentSet: "",
  searchText: "",
  searchFields: [],
};

const formatTimestamp = (ts) => {
  if (!ts) return "";
  // backend timestamps are UTC; ISO strings without a tz offset must be parsed as UTC
  const normalized =
    typeof ts === "string" && !/(?:Z|[+-]\d{2}:?\d{2})$/.test(ts)
      ? `${ts}Z`
      : ts;
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return ts;
  const formatted = date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
  return `${formatted} UTC`;
};

const generateRows = (data) =>
  (Array.isArray(data) ? data : []).map((item) => ({
    id: item?.id,
    timestamp: item?.timestamp ?? "",
    eventType: item?.event_type ?? "",
    details: item?.details ?? "",
    triggeredBy: item?.triggered_by ?? "",
    referenceId: item?.id ?? "",
  }));

export default function DataRetentionAuditLog() {
  const navigate = useNavigate();
  const selectedOrg = useSelector(
    (state) => state?.account?.value?.selectedOrg,
  );

  const [availableData, dispatchAvailableData] = useReducer(
    filtersReducer,
    initialState,
  );

  //States
  const [rows, setRows] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [pageData, setPageData] = useState({
    rowCount: 0,
    prev: null,
    next: null,
    curPage: 1,
    pageSize: 10,
  });
  const [, setGTablePageNumber] = useState(0);
  const [downloading, setDownloading] = useState(false);

  //Handlers
  const handleBack = () => navigate("/settings", { state: { tab: "Account" } });

  const fetchAuditLogs = () => {
    setShowLoader(true);
    const params = new URLSearchParams();
    params.append("page", pageData?.curPage || 1);
    params.append("page_size", pageData?.pageSize);

    const selectedEventTypes = availableData?.eventTypes?.selected;
    const selectedTriggeredBy = availableData?.triggeredBy?.selected;
    if (selectedEventTypes?.length)
      params.append("event_type", selectedEventTypes.join(","));
    if (selectedTriggeredBy?.length)
      params.append("triggered_by", selectedTriggeredBy.join(","));

    JService.get(`/api/v1/auth/data-retention/audit-logs/${getOrgURLParam()}?${params.toString()}`)
      .then((res) => {
        if (!("success" in res)) return;
        if (!res?.success)
          throw new Error(res?.message || "Could not retrieve audit logs");
        setShowLoader(false);
        const results = res?.data?.results ?? [];
        setRows(generateRows(results));
        setPageData((prev) => ({
          ...prev,
          rowCount: res?.data?.count,
          next: res?.data?.next,
          prev: res?.data?.previous,
        }));
      })
      .catch((err) => {
        enqueueSnackbar(err?.message || "Failed to fetch audit logs", {
          variant: "error",
        });
        setRows([]);
        setPageData((prev) => ({
          ...prev,
          rowCount: 0,
          next: null,
          prev: null,
        }));
      })
      .finally(() => setShowLoader(false));
  };

  const handlePageChange = (val) => {
    setPageData((prev) => ({ ...prev, curPage: val + 1 }));
  };

  const handlePageSizeChange = (pageSize) => {
    setPageData((prev) => ({ ...prev, pageSize: pageSize, curPage: 1 }));
    setGTablePageNumber(0);
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      const selectedEventTypes = availableData?.eventTypes?.selected;
      const selectedTriggeredBy = availableData?.triggeredBy?.selected;
      if (selectedEventTypes?.length)
        params.append("event_type", selectedEventTypes.join(","));
      if (selectedTriggeredBy?.length)
        params.append("triggered_by", selectedTriggeredBy.join(","));
      const query = params.toString();

      const data = await JService.get(
        `/api/v1/auth/data-retention/audit-logs/export/${getOrgURLParam()}${
          query ? `?${query}` : ""
        }`,
        {},
        { responseType: "blob" },
      );

      const today = new Date().toISOString().slice(0, 10);
      const filename = selectedOrg
        ? `retention_audit_log_org${selectedOrg}_${today}.csv`
        : `retention_audit_log_${today}.csv`;

      const url = window.URL.createObjectURL(
        new Blob([data], { type: "text/csv" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to download audit logs", {
        variant: "error",
      });
    } finally {
      setDownloading(false);
    }
  };

  //Effects
  useEffect(() => {
    JService.get(`/api/v1/auth/retrieve-users/${getOrgURLParam()}`)
      .then((res) => {
        if (!res?.success) return;
        const emails = (res?.data || [])
          .map((user) => user?.email)
          .filter(Boolean)
          .map((email) => ({ id: email, name: email }));
        dispatchAvailableData({
          type: "setPayloadToAllOfType",
          payload: {
            payloadFor: "triggeredBy",
            payload: [{ id: "system", name: "System" }, ...emails],
          },
        });
      })
      .catch((err) => console.warn(err?.message || "Failed to fetch users"));
  }, []);

  useEffect(() => {
    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageData.pageSize,
    pageData.curPage,
    availableData?.eventTypes?.selected,
    availableData?.triggeredBy?.selected,
    selectedOrg
  ]);

  useEffect(() => {
    setPageData((prev) =>
      prev.curPage === 1 ? prev : { ...prev, curPage: 1 },
    );
    setGTablePageNumber(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    availableData?.eventTypes?.selected,
    availableData?.triggeredBy?.selected,
  ]);

  const cols = useMemo(
    () => [
      {
        field: "timestamp",
        headerClassName: "audit-log-header",
        headerName: "Timestamp",
        width: 220,
        renderCell: (params) => (
          <span>{formatTimestamp(params?.row?.timestamp)}</span>
        ),
      },
      {
        field: "eventType",
        headerName: "Event type",
        width: 160,
        renderCell: (params) => (
          <span>
            {EVENT_TYPE_LABELS[params?.row?.eventType] ??
              params?.row?.eventType}
          </span>
        ),
      },
      {
        field: "details",
        headerName: "Details",
        width: 500,
        renderCell: (params) => (
          <p
            className="audit-log-cells-no-overflow"
            title={params?.row?.details}
          >
            {params?.row?.details}
          </p>
        ),
      },
      {
        field: "triggeredBy",
        headerName: "Triggered by",
        width: 220,
        renderCell: (params) => (
          <span
            className="audit-log-cells-no-overflow"
            title={params?.row?.triggeredBy}
          >
            {params?.row?.triggeredBy === "system"
              ? "System"
              : params?.row?.triggeredBy}
          </span>
        ),
      },
      {
        field: "referenceId",
        headerName: "Reference ID",
        width: 240,
        renderCell: (params) => <span>ID-{params?.row?.referenceId}</span>,
      },
    ],
    [],
  );

  return (
    <div className="screen-container">
      <button className="audit-log-back" onClick={handleBack}>
        {chevronLeft("20", "20", "#2AA251")} Back
      </button>

      <h5 className="audit-log-heading">Audit log</h5>
      <div className="audit-log-separator" />

      <div className="audit-log-filter-section">
        <Filters
          filtersList={filtersList}
          dispatchAvailableData={dispatchAvailableData}
          availableData={availableData}
          dateFilter={false}
          style={{ marginTop: 0, marginBottom: 0 }}
        />
        <Button
          variant="outlined"
          hierarchy="white"
          buttonText="Download"
          icon={{ startIcon: downloadIcon() }}
          onClick={handleDownload}
          isLoading={downloading}
        />
      </div>

      <GTable
        className="audit-log-gtable"
        leftHeader={{ filters: [], isSearch: false }}
        rightHeader={{}}
        hideHeader={true}
        rows={rows}
        columns={cols}
        useMuiTable={true}
        isLoading={showLoader}
        onRowClick={() => null}
        availableData={availableData}
        dispatchAvailableData={dispatchAvailableData}
        paginationMode="server"
        rowCount={pageData.rowCount || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        setGTablePageNumber={setGTablePageNumber}
        fromScreen="audit-logs"
      />
    </div>
  );
}
